import { NavLink, useNavigate } from 'react-router-dom';
import { Home, Layers, Settings, LogOut, BarChart3, Trophy, Bell, ChevronRight, ChevronLeft, X, Star, Shield } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { getUnreadCount } from '../utils/notificationHistory';
import { useAuth } from '../hooks/useAuth';

const navItemsBase = [
  { label: 'Обзор', to: '/', icon: <Home size={20} /> },
  { label: 'Устройства', to: '/devices', icon: <Layers size={20} /> },
  { label: 'Отчеты', to: '/reports', icon: <BarChart3 size={20} /> },
  { label: 'Лидеры', to: '/leaders', icon: <Trophy size={20} /> },
  { label: 'Уведомления', to: '/notifications', icon: <Bell size={20} /> },
  { label: 'Настройки', to: '/settings', icon: <Settings size={20} /> },
];

interface SidebarProps {
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
  mobileOpen?: boolean;
  setMobileOpen?: (v: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, setCollapsed, mobileOpen = false, setMobileOpen }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.is_superuser === 1;
  const [unread, setUnread] = useState<number>(0);
  const [mskTime, setMskTime] = useState<string>('');
  const navItems = isAdmin
    ? [...navItemsBase, { label: 'Админка', to: '/admin', icon: <Shield size={20} /> }]
    : navItemsBase;
  useEffect(() => {
    const load = async () => {
      try { setUnread(await getUnreadCount()); } catch {}
    };
    load();
    const onUpd = () => load();
    window.addEventListener('notification-unread-updated', onUpd);
    window.addEventListener('notification-history-updated', onUpd);
    return () => {
      window.removeEventListener('notification-unread-updated', onUpd);
      window.removeEventListener('notification-history-updated', onUpd);
    };
  }, []);
  useEffect(() => {
    const update = () => {
      try {
        const now = new Date();
        const str = new Intl.DateTimeFormat('ru-RU', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
          timeZone: 'Europe/Moscow',
        }).format(now);
        setMskTime(str);
      } catch {}
    };
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, []);
  const handleLogout = () => {
    try {
      localStorage.removeItem('access_token');
    } catch {}
    navigate('/login');
    window.location.reload();
  };
  return (
    <>
      {/* Десктопная боковая панель */}
      <div className="hidden md:block">
        <aside className={`h-screen ${collapsed ? 'w-20' : 'w-64'} bg-[#181A20] border-r border-[#23262F] flex flex-col fixed left-0 top-0 z-40 transition-all duration-300`}>
          <div className="h-20 flex flex-col items-center justify-center border-b border-[#23262F] relative">
            <button
              className="absolute -right-3 top-1/2 transform -translate-y-1/2 bg-[#23262F] border border-[#353945] rounded-full p-1 text-white hover:bg-[#353945] transition-colors"
              onClick={() => setCollapsed(!collapsed)}
            >
              {collapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
            </button>
            {collapsed ? (
              <span className="text-white"><Star size={22} /></span>
            ) : (
              <>
                <span className="text-2xl font-bold text-white tracking-wide">GuruApp</span>
                <span className="text-sm font-bold text-white">Hub</span>
              </>
            )}
          </div>
          <nav className="flex-1 py-6 px-2 space-y-2">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `flex items-center px-4 py-2 rounded-lg transition-colors duration-200 font-medium
                 ${collapsed ? 'justify-center' : 'gap-3'}
                 ${isActive ? 'bg-[#23262F] text-white' : 'text-[#B1B5C3] hover:bg-[#23262F] hover:text-white'}`
                }
                end={item.to === '/'}
              >
                <span className="w-6 h-6 flex items-center justify-center relative">
                  {item.icon}
                  {item.to === '/notifications' && unread > 0 ? (
                    <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-2.5 h-2.5 rounded-full bg-blue-500"></span>
                  ) : null}
                </span>
                <span className={`${collapsed ? 'hidden' : 'inline'} transition-all duration-300`}>
                  {item.label}
                </span>
              </NavLink>
            ))}
          </nav>
          {/* Время сервера (МСК) — над нижней панелью с кнопкой выхода */}
          <div className="px-4 py-3">
            <div className="text-xs text-center">
              {!collapsed ? <span className="text-[#B1B5C3]">Время сервера: </span> : null}
              <span className="text-white font-mono">{mskTime}</span>
            </div>
          </div>
          {!(typeof window !== 'undefined' && (window as any).Telegram?.WebApp) && (
            <div className="p-4 border-t border-[#23262F]">
              <button
                onClick={handleLogout}
                className={`w-full flex items-center px-4 py-2 rounded-lg text-[#B1B5C3] hover:bg-[#23262F] hover:text-white transition-colors duration-200 font-medium ${collapsed ? 'justify-center' : 'gap-3'}`}
              >
                <span className="w-6 h-6 flex items-center justify-center"><LogOut size={20} /></span>
                <span className={`${collapsed ? 'hidden' : 'inline'} transition-all duration-300`}>Выйти</span>
              </button>
            </div>
          )}
        </aside>
      </div>

      {/* Мобильное полноэкранное меню */}
      <div className={`${mobileOpen ? 'fixed' : 'hidden'} inset-0 z-50 md:hidden`}>
        <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen && setMobileOpen(false)} />
        <aside className="absolute inset-0 bg-[#181A20] flex flex-col">
          {/* Safe area spacer for Telegram Mini Apps */}
          <div className="flex-shrink-0 md:hidden" style={{ height: 'calc(env(safe-area-inset-top, 0px) + var(--tg-safe-area-inset-top, 0px))' }} />
          <div className="h-16 px-4 flex items-center justify-between border-b border-[#23262F]">
            <span className="text-white font-bold text-lg">Меню</span>
            <button
              className="p-2 rounded-lg bg-[#23262F] border border-[#353945] text-white"
              onClick={() => setMobileOpen && setMobileOpen(false)}
              aria-label="Закрыть меню"
            >
              <X size={20} />
            </button>
          </div>
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors duration-200 font-medium
                  ${isActive ? 'bg-[#23262F] text-white' : 'text-[#B1B5C3] hover:bg-[#23262F] hover:text-white'}`
                }
                end={item.to === '/'}
                onClick={() => setMobileOpen && setMobileOpen(false)}
              >
                <span className="w-6 h-6 flex items-center justify-center relative">
                  {item.icon}
                  {item.to === '/notifications' && unread > 0 ? (
                    <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-2.5 h-2.5 rounded-full bg-blue-500"></span>
                  ) : null}
                </span>
                <span className="inline">{item.label}</span>
              </NavLink>
            ))}
          </nav>
          {/* Время сервера (МСК) — над нижней панелью с кнопкой выхода */}
          <div className="px-4 py-3">
            <div className="text-xs text-center">
              <span className="text-[#B1B5C3]">Время сервера: </span>
              <span className="text-white font-mono">{mskTime}</span>
            </div>
          </div>
          {!(typeof window !== 'undefined' && (window as any).Telegram?.WebApp) && (
            <div className="p-4 border-t border-[#23262F]">
              <button
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-[#B1B5C3] hover:bg-[#23262F] hover:text-white transition-colors duration-200 font-medium"
                onClick={() => { setMobileOpen && setMobileOpen(false); handleLogout(); }}
              >
                <span className="w-6 h-6 flex items-center justify-center"><LogOut size={20} /></span>
                <span className="inline">Выйти</span>
              </button>
            </div>
          )}
        </aside>
      </div>
    </>
  );
};

export default Sidebar; 